<template>
  <div class="card product-card">
    <!-- Visual Image Box with Free Delivery Badge -->
    <div
      @click="Configure"
      class="imgbox cursor-pointer">
      <span
        class="fd"
        v-if="free_shipping">
        Free delivery
      </span>
      <span
        class="bs"
        v-if="is_best_seller">
        ★ Best Seller
      </span>
      <img
        :alt="product.name"
        v-if="product.product_image"
        :src="ProductImage(product)">
      <div v-else class="stage-placeholder">
        <svg
          class="stage-icon"
          aria-hidden="true"
          viewBox="0 0 64 64">
          <rect x="14" y="24" width="36" height="23" rx="2" />
          <path d="M19 16h26l-3-8H22l-3 8z" />
          <rect x="16" y="16" width="32" height="8" rx="1" />
          <line x1="18" y1="29" x2="34" y2="29" />
          <rect x="38" y="27" width="8" height="6" rx="1" />
          <line x1="14" y1="36" x2="50" y2="36" />
          <line x1="14" y1="41" x2="50" y2="41" />
          <rect x="28" y="37.5" width="8" height="1.5" rx="0.75" />
          <rect x="28" y="42.5" width="8" height="1.5" rx="0.75" />
          <line x1="18" y1="47" x2="18" y2="53" />
          <line x1="46" y1="47" x2="46" y2="53" />
          <circle cx="18" cy="54" r="1.5" />
          <circle cx="46" cy="54" r="1.5" />
        </svg>
      </div>
    </div>

    <!-- Centered Spec Capsule Overlapping Image Box Bottom -->
    <span
      ref="spec_el"
      class="spec"
      :title="is_truncated ? product_specs_summary : undefined"
      @mouseenter="CheckSpecOverflow"
      v-if="product_specs_summary">
      {{ product_specs_summary }}
    </span>

    <!-- Product Title -->
    <a
      :href="product_url"
      class="product-title-link">
      <div class="nm">{{ product.name }}</div>
    </a>

    <!-- Service & Toner Included Subtitle -->
    <div
      class="inc"
      v-if="is_quotable">
      ✓ Service &amp; toner included
    </div>

    <!-- Action CTA Button -->
    <button
      class="cta"
      type="button"
      @click="Configure">
      <template v-if="should_show_pricing">
        <span class="e">EST.</span>
        <span class="p">${{ LowestMonthlyPrice }}<small>/mo</small></span>
      </template>
      <template v-else>
        <span class="p">Request Pricing</span>
      </template>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useGlobalsStore } from '@/stores/globals'
import { CalculateProductMonthlyPrice, IsProductCustomQuote, IsProductQuotable } from '@/mixins/PricingMixins.ts'
import { FormatProductSpecsSummary } from '@/mixins/ProductMixins.ts'

const props = defineProps<{product: any}>();
const store = useGlobalsStore()
const { free_shipping, run_preapprovals, partner_identifier } = storeToRefs(store)

const spec_el = ref<HTMLElement | null>(null)
const is_truncated = ref(false)

function CheckSpecOverflow() {
  if (spec_el.value) {
    is_truncated.value = spec_el.value.scrollWidth > (spec_el.value.clientWidth + 1)
  }
}

function Configure() {
  store.ConfigureProduct(props.product)
}

const ProductImage = store.ProductImage

const is_quotable = computed(() => {
  return IsProductQuotable(props.product)
})

const product_specs_summary = computed(() => {
  const summary = FormatProductSpecsSummary(props.product)
  if (!summary || summary === '-' || summary.toLowerCase() === 'n/a' || summary.trim() === '') {
    return ''
  }
  return summary.trim()
})

const LowestMonthlyPrice = computed(() => {
  return CalculateProductMonthlyPrice(props.product)
})

const is_unpriced = computed(() => {
  return IsProductCustomQuote(props.product)
})

/**
 * Partner '1' represents the SaaS owner (1800 Office Solutions / flagship site).
 * Pricing is always exposed on the flagship showroom regardless of whether
 * run_preapprovals is enabled, whereas external white-label partner sites
 * adhere strictly to the run_preapprovals configuration setting.
 */
const is_partner_one = computed(() => {
  const pid = partner_identifier?.value ?? (window as any).catalog_config?.partner_identifier
  return String(pid) === '1'
})

const should_show_pricing = computed(() => {
  const allow_pricing = is_partner_one.value || Boolean(run_preapprovals.value)
  return allow_pricing && is_quotable.value && !is_unpriced.value && LowestMonthlyPrice.value > 0
})

const is_best_seller = computed(() => {
  if (props.product?.best_seller) return true
  const best_sellers: any[] = (window as any).catalog_config?.best_sellers || []
  return best_sellers.some((id: any) => Number(id) === Number(props.product?.id))
})

const product_url = computed(() => {
  const base_url = ((window as any).catalog_config?.single_page_url || '/product/').replace(/\/+$/, '')
  const slug = props.product?.slug || ''
  return `${base_url}/${slug}`
})
</script>

<style scoped lang="scss">
.card {
  padding: 0;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid #e8ecf1;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    border-color: #cbd5e1;
    box-shadow: 0 12px 24px -4px rgba(11, 18, 32, 0.08);
  }
}

.imgbox {
  position: relative;
  border-radius: 0;
  height: 170px;
  padding: 12px 14px 14px;
  box-sizing: border-box;
  background: #f3f4f6;
  border-bottom: 1px solid #e6e9ee;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    max-height: 170px;
    max-width: 80%;
    width: auto;
    height: auto;
    object-fit: contain;
    margin: auto;
    display: block;
    mix-blend-mode: multiply;
  }
}

.fd {
  position: absolute;
  left: 14px;
  top: 14px;
  z-index: 2;
  background: #ecfdf3;
  border: 1px solid #b9e5c9;
  color: #15803d;
  font-size: 11.5px;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 11px;
  line-height: 1.2;
  white-space: nowrap;
}

.bs {
  position: absolute;
  right: 14px;
  top: 14px;
  z-index: 2;
  background: #fef3c7;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 3px 10px;
  line-height: 1.2;
  white-space: nowrap;
}

.stage-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  .stage-icon {
    width: 84px;
    height: 84px;
    stroke: #94a3b8;
    fill: none;
    stroke-width: 2.2;
    stroke-linecap: round;
    stroke-linejoin: round;
    opacity: 0.8;
  }
}

.spec {
  display: inline-block;
  align-self: center;
  margin-top: -13px;
  position: relative;
  z-index: 2;
  background: #ffffff;
  color: #334155;
  border: 1px solid #e1e5eb;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 5px 12px;
  max-width: calc(100% - 32px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.product-title-link {
  text-decoration: none;
  color: inherit;
  display: block;
  margin-bottom: 5px;
}

.nm {
  text-align: center;
  margin: 12px 16px 0;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.22;
  letter-spacing: -0.01em;
  min-height: 42px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #0b1220;
  transition: color 0.15s ease;

  &:hover {
    color: #1b5fd1;
  }
}

.inc {
  text-align: center;
  font-size: 13px;
  display: block;
  margin-bottom: 5px;
  font-weight: 600;
  color: #15803d;
}

.cta {
  margin: 14px 14px 14px;
  background: #1b5fd1;
  color: #ffffff;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 6px;
  white-space: nowrap;
  cursor: pointer;
  border: none;
  width: calc(100% - 28px);
  margin-top: auto;
  transition: background-color 0.15s ease;

  &:hover {
    background: #154cab;
  }

  .e {
    font-family: Inter, system-ui, -apple-system, sans-serif;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.08em;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 4px;
    padding: 1px 4px;
    transform: translateY(-2px);
  }

  .p {
    font-size: 17px;
    font-weight: 800;
    letter-spacing: -0.01em;

    small {
      font-size: 12px;
      font-weight: 600;
      opacity: 0.85;
    }
  }
}
</style>
